import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Package,
  Check,
  X,
  Truck,
  Clock,
  AlertCircle,
  User,
  Calendar,
  FileText,
  Edit2
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getTransferById,
  approveTransfer,
  rejectTransfer,
  shipTransfer,
  receiveTransfer,
  cancelTransfer
} from '../../api/stockTransfers';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const TransferDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'retail_admin', 'system_admin'].includes(user?.role);

  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [rejectModal, setRejectModal] = useState({ isOpen: false, reason: '' });
  const [shipModal, setShipModal] = useState({ isOpen: false, items: [], trackingRef: '' });
  const [receiveModal, setReceiveModal] = useState({ isOpen: false, items: [] });

  const fetchTransfer = useCallback(async () => {
    try {
      const data = await getTransferById(id);
      setTransfer(data);

      // Initialize ship/receive items from transfer items
      if (data.items) {
        setShipModal((prev) => ({
          ...prev,
          items: data.items.map((item) => ({
            productId: item.product_id,
            productName: item.product_name,
            requested: item.requested_quantity,
            quantity: item.shipped_quantity ?? item.requested_quantity
          }))
        }));
        setReceiveModal((prev) => ({
          ...prev,
          items: data.items.map((item) => ({
            productId: item.product_id,
            productName: item.product_name,
            shipped: item.shipped_quantity,
            quantity: item.received_quantity ?? item.shipped_quantity ?? 0,
            discrepancyNotes: ''
          }))
        }));
      }
    } catch (error) {
      toast.error('Failed to load transfer');
      navigate('/stock-transfers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchTransfer();
  }, [fetchTransfer]);

  // Action handlers
  const handleApprove = async () => {
    if (!window.confirm('Approve this transfer?')) return;
    setActionLoading(true);
    try {
      await approveTransfer(id);
      toast.success('Transfer approved');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setActionLoading(true);
    try {
      await rejectTransfer(id, rejectModal.reason);
      toast.success('Transfer rejected');
      setRejectModal({ isOpen: false, reason: '' });
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleShip = async () => {
    setActionLoading(true);
    try {
      await shipTransfer(
        id,
        shipModal.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        shipModal.trackingRef || undefined
      );
      toast.success('Transfer shipped');
      setShipModal((prev) => ({ ...prev, isOpen: false }));
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to ship');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceive = async () => {
    setActionLoading(true);
    try {
      await receiveTransfer(
        id,
        receiveModal.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          discrepancyNotes: item.discrepancyNotes || undefined
        }))
      );
      toast.success('Transfer received');
      setReceiveModal((prev) => ({ ...prev, isOpen: false }));
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to receive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    const reason = window.prompt('Reason for cancellation:');
    if (reason === null) return;
    setActionLoading(true);
    try {
      await cancelTransfer(id, reason);
      toast.success('Transfer cancelled');
      fetchTransfer();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  // Status badge
  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      partially_received: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Draft',
      pending: 'Pending Approval',
      approved: 'Approved',
      in_transit: 'In Transit',
      partially_received: 'Partially Received',
      completed: 'Completed',
      cancelled: 'Cancelled',
      rejected: 'Rejected'
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100'}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  // Priority badge
  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600',
      normal: 'bg-blue-100 text-blue-600',
      high: 'bg-orange-100 text-orange-600',
      urgent: 'bg-red-100 text-red-600'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[priority]}`}>
        {priority?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!transfer) return null;

  // Higher-level admins who can always approve/receive
  const higherLevelAdmins = ['super_admin', 'system_admin', 'retail_admin'];
  const isHigherLevelAdmin = higherLevelAdmins.includes(user?.role);

  // Check if user is the receiving branch admin
  const isReceivingBranchAdmin =
    user?.role === 'branch_admin' && user?.current_branch_id === transfer.to_branch_id;

  // Check if user is the sending branch admin
  const isSendingBranchAdmin =
    user?.role === 'branch_admin' && user?.current_branch_id === transfer.from_branch_id;

  const canApprove = isAdmin && transfer.status === 'pending';
  const canShip =
    ['approved'].includes(transfer.status) && (isHigherLevelAdmin || isSendingBranchAdmin);
  const canReceive =
    ['in_transit', 'partially_received'].includes(transfer.status) &&
    (isHigherLevelAdmin || isReceivingBranchAdmin);
  const canCancel = ['draft', 'pending', 'approved'].includes(transfer.status);

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/stock-transfers')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl sm:text-2xl font-bold">{transfer.transfer_number}</h1>
                  {getStatusBadge(transfer.status)}
                  {getPriorityBadge(transfer.priority)}
                </div>
                <div className="flex items-center gap-2 text-gray-500 mt-1">
                  <span className="font-medium">{transfer.from_branch_name}</span>
                  <ArrowRight className="w-4 h-4" />
                  <span className="font-medium">{transfer.to_branch_name}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {canApprove && (
                <>
                  <button
                    onClick={handleApprove}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => setRejectModal({ isOpen: true, reason: '' })}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
              {canShip && (
                <button
                  onClick={() => setShipModal((prev) => ({ ...prev, isOpen: true }))}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  <Truck className="w-4 h-4" />
                  Ship Transfer
                </button>
              )}
              {canReceive && (
                <button
                  onClick={() => setReceiveModal((prev) => ({ ...prev, isOpen: true }))}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <Package className="w-4 h-4" />
                  Receive Items
                </button>
              )}
              {canCancel && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Transfer Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-sm text-gray-500 mb-2">Initiated By</h3>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{transfer.initiated_by_name}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {new Date(transfer.initiated_at).toLocaleString()}
            </p>
          </div>

          {transfer.approved_by && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm text-gray-500 mb-2">
                {transfer.status === 'rejected' ? 'Rejected By' : 'Approved By'}
              </h3>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{transfer.approved_by_name}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(transfer.approved_at).toLocaleString()}
              </p>
            </div>
          )}

          {transfer.shipped_by && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm text-gray-500 mb-2">Shipped By</h3>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{transfer.shipped_by_name}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(transfer.shipped_at).toLocaleString()}
              </p>
            </div>
          )}

          {transfer.received_by && (
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="text-sm text-gray-500 mb-2">Received By</h3>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{transfer.received_by_name}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(transfer.received_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Rejection Reason */}
        {transfer.rejection_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
              <AlertCircle className="w-4 h-4" />
              Rejection Reason
            </div>
            <p className="text-red-600">{transfer.rejection_reason}</p>
          </div>
        )}

        {/* Notes */}
        {transfer.notes && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <FileText className="w-4 h-4" />
              <h3 className="font-medium">Notes</h3>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{transfer.notes}</p>
          </div>
        )}

        {/* Items Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h3 className="font-semibold">Transfer Items ({transfer.items?.length || 0})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Product</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Requested</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Shipped</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Received</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold">Unit Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {transfer.items?.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-xs text-gray-500">{item.product_code}</p>
                    </td>
                    <td className="px-4 py-3 text-center font-medium">{item.requested_quantity}</td>
                    <td className="px-4 py-3 text-center">
                      {item.shipped_quantity !== null ? (
                        <span
                          className={
                            item.shipped_quantity < item.requested_quantity ? 'text-orange-600' : ''
                          }
                        >
                          {item.shipped_quantity}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.received_quantity !== null ? (
                        <span
                          className={
                            item.received_quantity < (item.shipped_quantity || 0)
                              ? 'text-orange-600'
                              : 'text-green-600'
                          }
                        >
                          {item.received_quantity}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      ₦{parseFloat(item.unit_cost || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Reject Transfer</h2>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg resize-none"
              rows={3}
              placeholder="Reason for rejection..."
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setRejectModal({ isOpen: false, reason: '' })}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ship Modal */}
      {shipModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-bold">Ship Transfer</h2>
              <p className="text-sm text-gray-500">Confirm quantities being shipped</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Tracking Reference (Optional)
                </label>
                <input
                  type="text"
                  value={shipModal.trackingRef}
                  onChange={(e) =>
                    setShipModal((prev) => ({ ...prev, trackingRef: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., delivery driver name, vehicle number"
                />
              </div>
              <div className="space-y-3">
                {shipModal.items.map((item, idx) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{item.productName}</p>
                      <p className="text-xs text-gray-500">Requested: {item.requested}</p>
                    </div>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        const newItems = [...shipModal.items];
                        newItems[idx].quantity = Math.min(
                          parseInt(e.target.value) || 0,
                          item.requested
                        );
                        setShipModal((prev) => ({ ...prev, items: newItems }));
                      }}
                      className="w-20 px-2 py-1 border rounded text-center"
                      min="0"
                      max={item.requested}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-3">
              <button
                onClick={() => setShipModal((prev) => ({ ...prev, isOpen: false }))}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleShip}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {actionLoading ? 'Shipping...' : 'Confirm Ship'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {receiveModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b">
              <h2 className="text-xl font-bold">Receive Items</h2>
              <p className="text-sm text-gray-500">Confirm quantities received</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {receiveModal.items.map((item, idx) => (
                  <div key={item.productId} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{item.productName}</p>
                        <p className="text-xs text-gray-500">Shipped: {item.shipped}</p>
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newItems = [...receiveModal.items];
                          newItems[idx].quantity = parseInt(e.target.value) || 0;
                          setReceiveModal((prev) => ({ ...prev, items: newItems }));
                        }}
                        className="w-20 px-2 py-1 border rounded text-center"
                        min="0"
                      />
                    </div>
                    {item.quantity < item.shipped && (
                      <input
                        type="text"
                        value={item.discrepancyNotes}
                        onChange={(e) => {
                          const newItems = [...receiveModal.items];
                          newItems[idx].discrepancyNotes = e.target.value;
                          setReceiveModal((prev) => ({ ...prev, items: newItems }));
                        }}
                        className="w-full px-2 py-1 border rounded text-sm mt-2"
                        placeholder="Reason for discrepancy..."
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-3">
              <button
                onClick={() => setReceiveModal((prev) => ({ ...prev, isOpen: false }))}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReceive}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Processing...' : 'Confirm Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferDetailPage;
